import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

const unsavedEditors = new Set()
let refreshing = false

window.addEventListener('beforeunload', (event) => {
  if (unsavedEditors.size === 0) return
  event.preventDefault()
  event.returnValue = ''
})

document.addEventListener('turbo:before-visit', (event) => {
  if (refreshing || unsavedEditors.size === 0) return
  if (!window.confirm('Há alterações não salvas. Sair mesmo assim?')) event.preventDefault()
})

document.addEventListener('turbo:load', () => {
  refreshing = false
})

// Saves only on ✓ or Enter. Every state lives in slots that are always laid out
// (visibility, not display) so nothing on the page shifts while editing.
export default class extends Controller {
  static targets = ['input', 'actions', 'saved', 'idle', 'error']
  static classes = ['clean', 'dirty', 'invalid']
  static values = { url: String, params: Object, refresh: Boolean }

  connect() {
    this.persisted = this.inputTarget.value
    this.render()
  }

  disconnect() {
    unsavedEditors.delete(this)
    clearTimeout(this.savedTimer)
  }

  edit() {
    this.hideError()
    this.render()
  }

  keydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.save()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.cancel()
    }
  }

  cancel() {
    this.inputTarget.value = this.persisted
    this.hideError()
    this.render()
  }

  async save() {
    if (!this.dirty || this.saving) return

    this.saving = true
    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: 'application/json',
        },
        body: new URLSearchParams({ ...this.paramsValue, [this.inputTarget.name]: this.inputTarget.value }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || data.status !== 'success') throw new Error(data.message || 'Não foi possível salvar.')

      this.persisted = this.inputTarget.value
      this.render()
      this.refreshValue ? this.refreshPage() : this.flashSaved()
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.saving = false
    }
  }

  // A morph refresh would put a half-typed value in another field back to what
  // the server has; skipping that field keeps the edit.
  preserveUnsaved(event) {
    if (this.dirty && this.element.contains(event.target)) event.preventDefault()
  }

  refreshPage() {
    refreshing = true
    const done = () => {
      this.persisted = this.inputTarget.value
      this.render()
      this.flashSaved()
    }
    document.addEventListener('turbo:morph', done, { once: true })
    Turbo.visit(window.location.href, { action: 'replace', refresh: { method: 'morph', scroll: 'preserve' } })
  }

  flashSaved() {
    clearTimeout(this.savedTimer)
    this.toggle(this.savedTargets, true)
    this.toggle(this.idleTargets, false)
    this.savedTimer = setTimeout(() => {
      this.toggle(this.savedTargets, false)
      this.render()
    }, 2000)
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
    this.swapClasses(this.dirtyClasses, false)
    this.swapClasses(this.invalidClasses, true)
  }

  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.classList.add('hidden')
    this.swapClasses(this.invalidClasses, false)
  }

  render() {
    const dirty = this.dirty
    dirty ? unsavedEditors.add(this) : unsavedEditors.delete(this)

    this.swapClasses(this.dirtyClasses, dirty)
    this.swapClasses(this.cleanClasses, !dirty)
    this.toggle(this.actionsTargets, dirty)
    this.toggle(this.idleTargets, !dirty && !this.savedTargets.some((el) => !el.classList.contains('invisible')))
  }

  get dirty() {
    return this.inputTarget.value !== this.persisted
  }

  toggle(elements, visible) {
    elements.forEach((element) => element.classList.toggle('invisible', !visible))
  }

  swapClasses(classes, on) {
    classes.forEach((name) => this.inputTarget.classList.toggle(name, on))
  }
}
